/**
 * ICS-202 Generate: open Pratus AI with an editable prompt. Send starts a
 * server run that drafts each box from the data sources picked in the composer,
 * a few boxes at a time. Boxes being written are locked for every collaborator
 * and unlock as they land.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { SparklesIcon } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import config from 'constants/apiConfig';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { pollDChatSuccess } from 'slices/dchatSlice';
import type { IcsFormFamily } from 'components/IAP/formFamily';
import {
  useChatSidebar,
  type ComposerSendContext,
} from 'components/GlobalChatSidebar/ChatSidebarProvider';
import { ClickableFieldCitations } from 'components/DisasterGPT/fieldDraft/ClickableFieldCitations';
import { useAiFieldProvenance } from 'components/DisasterGPT/fieldDraft/useAiFieldProvenance';

import { useICSContext } from '../planningView/ICSContext';
import { useActualIncidentId } from '../hooks/commonHooks';
import { useIcsFormFamily } from '../hooks/useIcsFormFamily';
import { Ics202DraftProvider, useIcs202Draft } from './Ics202DraftContext';
import { Ics202LockedBox } from './Ics202LockedBox';
import './ics202DraftEdge.css';
import {
  buildIcs202CannedPrompt,
  ics202DraftChipLabel,
} from './ics202DraftPrompt';
import {
  ics202Keys,
  invalidateIcs202Boxes,
  releasedFields,
  useIcs202AiLocks,
  useIcs202FamilyFields,
  useIcs202Run,
  type Ics202AiLock,
} from './useIcs202AiLocks';

const EMPTY_SOURCES: ReadonlySet<string> = new Set();
const EMPTY_FAILURES: ReadonlyMap<string, string> = new Map();

type PendingDraft = { sources: string[]; label: string };

type RunStarted = { runId: string; fields: string[] };

type DchatState = {
  dchat: { dchat?: { id?: string }; current_sitrep_id?: string };
};

export const Ics202DraftFlow: React.FC<{
  disabled?: boolean;
  family?: IcsFormFamily;
  children: ReactNode;
}> = ({ disabled = false, family: familyProp, children }) => {
  const incidentId = useActualIncidentId();
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const incidentFamily = useIcsFormFamily();
  const family = familyProp ?? incidentFamily;
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const openThreadId = useSelector(
    (state: DchatState) => state.dchat?.dchat?.id
  );
  const currentSitrepId = useSelector(
    (state: DchatState) => state.dchat?.current_sitrep_id
  );
  const {
    queueComposerPrompt,
    registerComposerSendHandler,
    isVisible,
    composerDraftLabel,
  } = useChatSidebar();

  const { data: familyFields = [] } = useIcs202FamilyFields(family);
  const allowedSources = useMemo(
    () => new Set(familyFields.map((field) => field.source)),
    [familyFields]
  );

  const [runId, setRunId] = useState<string | null>(null);
  const [threadIdForRun, setThreadIdForRun] = useState<string | null>(null);
  const [startingSources, setStartingSources] =
    useState<ReadonlySet<string>>(EMPTY_SOURCES);
  const { data: run } = useIcs202Run(runId, incidentId);
  const runActive = !!runId && (!run || run.status === 'running');
  const { data: locks = [] } = useIcs202AiLocks(
    incidentId,
    effectivePeriodNumber,
    runActive
  );

  const lockedSources = useMemo(
    () => new Set(locks.map((lock: Ics202AiLock) => lock.field)),
    [locks]
  );
  const generatingSources = useMemo<ReadonlySet<string>>(
    () =>
      startingSources.size === 0
        ? lockedSources
        : new Set([...lockedSources, ...startingSources]),
    [lockedSources, startingSources]
  );

  const previousLockedRef = useRef<ReadonlySet<string>>(EMPTY_SOURCES);
  useEffect(() => {
    const released = releasedFields(previousLockedRef.current, lockedSources);
    previousLockedRef.current = lockedSources;
    if (released.length > 0 && incidentId) {
      invalidateIcs202Boxes(queryClient, incidentId, released);
    }
  }, [lockedSources, incidentId, queryClient]);

  const failedSources = useMemo<ReadonlyMap<string, string>>(() => {
    if (!run) return EMPTY_FAILURES;
    const failures = new Map<string, string>();
    for (const [field, result] of Object.entries(run.results ?? {})) {
      if (result.status === 'failed') {
        failures.set(field, result.error || 'Drafting failed');
      }
    }
    return failures.size > 0 ? failures : EMPTY_FAILURES;
  }, [run]);

  const refreshThread = useCallback(
    async (threadId: string) => {
      try {
        const { data } = await axios.get(
          `${config.apiGateway.disasterchat}/poll`,
          {
            params: {
              id: threadId,
              dchat_id: threadId,
              user_guid: userGuid,
              group_guid: groupGuid,
              current_sitrep_id: currentSitrepId,
            },
          }
        );
        if (data) dispatch(pollDChatSuccess(data));
      } catch {
        // The summary is also in the toast; a stale thread is not an error.
      }
    },
    [userGuid, groupGuid, currentSitrepId, dispatch]
  );

  const announcedRunRef = useRef<string | null>(null);
  useEffect(() => {
    if (!run || run.status === 'running') return;
    if (announcedRunRef.current === run.id) return;
    announcedRunRef.current = run.id;
    if (incidentId) invalidateIcs202Boxes(queryClient, incidentId, run.fields);
    const summary = run.summary || 'ICS-202 generation finished.';
    if (run.status === 'completed') toast.success(summary);
    else if (run.status === 'partial') toast.info(summary);
    else toast.error(summary);
    if (threadIdForRun && threadIdForRun === openThreadId) {
      void refreshThread(threadIdForRun);
    }
  }, [
    run,
    incidentId,
    queryClient,
    threadIdForRun,
    openThreadId,
    refreshThread,
  ]);

  const startRun = useCallback(
    async (prompt: string, sources: string[], context: ComposerSendContext) => {
      if (!incidentId || sources.length === 0) return;
      setStartingSources(new Set(sources));
      try {
        const { data } = await axios.post<RunStarted>(
          config.apiGateway.ics202Runs,
          {
            incidentId,
            periodNumber: effectivePeriodNumber,
            family,
            fields: sources,
            prompt,
            selections: context.selections,
            threadId: context.threadId ?? null,
          }
        );
        setRunId(data.runId);
        setThreadIdForRun(context.threadId ?? null);
        await queryClient.invalidateQueries({
          queryKey: ics202Keys.locks(incidentId, effectivePeriodNumber),
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 409) {
          toast.info(
            'PRATUS AI is already generating some of these boxes. Try again when they unlock.'
          );
          void queryClient.invalidateQueries({
            queryKey: ics202Keys.locks(incidentId, effectivePeriodNumber),
          });
        } else {
          toast.error(
            err instanceof Error
              ? err.message
              : 'Could not start ICS-202 generation.'
          );
        }
      } finally {
        setStartingSources(EMPTY_SOURCES);
      }
    },
    [incidentId, effectivePeriodNumber, family, queryClient]
  );

  const pendingRef = useRef<PendingDraft | null>(null);
  const [pendingSources, setPendingSources] =
    useState<ReadonlySet<string>>(EMPTY_SOURCES);
  const clearPending = useCallback(() => {
    pendingRef.current = null;
    setPendingSources(EMPTY_SOURCES);
  }, []);

  useEffect(() => {
    registerComposerSendHandler((message, context) => {
      const pending = pendingRef.current;
      if (!pending?.sources.length) return false;
      clearPending();
      void startRun(message, pending.sources, context);
      return true;
    });
    return () => registerComposerSendHandler(null);
  }, [registerComposerSendHandler, startRun, clearPending]);

  useEffect(() => {
    if (!isVisible) clearPending();
  }, [isVisible, clearPending]);

  // The composer chip is the pending draft's handle: when it is cleared or
  // another prompt replaces it, the next send goes to the agent again.
  useEffect(() => {
    const pending = pendingRef.current;
    if (pending && composerDraftLabel !== pending.label) clearPending();
  }, [composerDraftLabel, clearPending]);

  const openDraft = useCallback(
    (nextSources?: string[], item?: string) => {
      if (!incidentId) {
        toast.error('Open an incident to draft the ICS 202.');
        return;
      }
      const sources = (
        nextSources?.length
          ? nextSources.filter((source) => allowedSources.has(source))
          : familyFields.map((field) => field.source)
      ).filter((source) => !generatingSources.has(source));
      if (sources.length === 0) return;

      const label = ics202DraftChipLabel(sources, item);
      pendingRef.current = { sources, label };
      setPendingSources(new Set(sources));
      queueComposerPrompt(buildIcs202CannedPrompt(family, sources), label);
    },
    [
      incidentId,
      family,
      familyFields,
      allowedSources,
      generatingSources,
      queueComposerPrompt,
    ]
  );

  const value = useMemo(
    () => ({
      openDraft,
      disabled: disabled || readOnlyForms,
      generatingSources,
      pendingSources,
      failedSources,
    }),
    [
      openDraft,
      disabled,
      readOnlyForms,
      generatingSources,
      pendingSources,
      failedSources,
    ]
  );

  return <Ics202DraftProvider value={value}>{children}</Ics202DraftProvider>;
};

/** Steady violet edge around one box while that box is waiting on the composer. */
export const Ics202DraftTarget: React.FC<{
  source: string;
  className?: string;
  children: ReactNode;
}> = ({ source, className, children }) => {
  const draft = useIcs202Draft();
  const pending = !!draft?.pendingSources.has(source);
  return (
    <div className={cn(className, pending && 'ics202-draft-edge')}>
      {children}
    </div>
  );
};

export const Ics202DraftButton: React.FC<{
  label?: string;
  sources?: string[];
}> = ({ label = 'Draft with AI', sources }) => {
  const draft = useIcs202Draft();
  if (!draft) return null;
  const generating = draft.generatingSources.size > 0;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={draft.disabled || generating}
      onClick={() => draft.openDraft(sources, 'ICS-202')}
      data-testid="ics-202-draft-ai"
    >
      <SparklesIcon className={generating ? 'tw:animate-spin' : undefined} />{' '}
      {label}
    </Button>
  );
};

export const Ics202SectionSparkle: React.FC<{
  source: string;
  label: string;
}> = ({ source, label }) => {
  const draft = useIcs202Draft();
  if (!draft || draft.disabled) return null;
  const generating = draft.generatingSources.has(source);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      aria-label={`Generate ${label}`}
      disabled={generating}
      onClick={() => draft.openDraft([source], label.replace(/:\s*$/, ''))}
    >
      <SparklesIcon className={generating ? 'tw:animate-spin' : undefined} />
    </Button>
  );
};

export const Ics202GeneratingBody: React.FC<{
  source: string;
  label?: string;
  children: ReactNode;
}> = ({ source, label, children }) => {
  const incidentId = useActualIncidentId();
  const { data: provenance = [] } = useAiFieldProvenance(
    'incident_info',
    incidentId
  );
  const row = provenance.find((item) => item.fieldKey === source);
  return (
    <Ics202LockedBox
      source={source}
      label={label}
      footer={
        row ? <ClickableFieldCitations citations={row.sources ?? []} /> : null
      }
    >
      {children}
    </Ics202LockedBox>
  );
};

export default Ics202DraftFlow;
